import { authenticatedSession } from "@shuttlerock/auth";
import { ConfigService } from "@shuttlerock/configuration";
import {
  getTrackingConsent,
  productAnalytics,
  subscribeTrackingConsent,
} from "@shuttlerock/observability";
import { useEffect } from "react";
import { useLocation, type createBrowserRouter } from "react-router";
import { analyticsRoute, redactPath } from "./analytics-route";

let rendered: ((pathname: string) => void) | undefined;

/**
 * Reports each pathname once React has rendered it. Called from the root layout, which error
 * boundaries render too, so every page counts, and the page view carries the page's own title.
 */
export function useAnalyticsPageView() {
  const { pathname } = useLocation();
  useEffect(() => rendered?.(pathname), [pathname]);
}

export function connectAnalytics(router: ReturnType<typeof createBrowserRouter>) {
  productAnalytics.configure({
    token: ConfigService.MIXPANEL_TOKEN(),
    region: ConfigService.MIXPANEL_REGION(),
    redactPath,
  });

  // An unreadable build version reads as "unknown", which is left out rather than sent.
  const version = ConfigService.APP_VERSION();
  const appVersion = version === "unknown" ? undefined : version;
  let renderedPathname: string | undefined;

  const update = () => {
    // The router moves to a new location before React renders it; wait for the render.
    if (!router.state.initialized || router.state.location.pathname !== renderedPathname) return;
    const principalKey = authenticatedSession.getSnapshot()?.principalKey;
    productAnalytics.update({
      ...analyticsRoute(router.state),
      trackingAllowed: getTrackingConsent(principalKey).allowed,
      principalKey,
      environment: ConfigService.ENVIRONMENT(),
      appVersion,
    });
  };

  rendered = (pathname) => {
    renderedPathname = pathname;
    update();
  };
  const unsubscribeSession = authenticatedSession.subscribe(update);
  const unsubscribeConsent = subscribeTrackingConsent(update);
  return () => {
    rendered = undefined;
    unsubscribeSession();
    unsubscribeConsent();
  };
}
